import {
  CurrentPageContainer,
  PageButton,
} from '@components/Pagination/pagination.styled';
import { Select } from '@components/Select/Select';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Button, SizableText, View, XStack } from 'tamagui';

interface PaginationProps {
  /** Current page number (1-based) */
  page: number;
  /** Total number of pages */
  count: number;
  /** Callback when page changes */
  onChange: (page: number) => void;
  /** Number of sibling buttons to show on each side of current page */
  siblingCount?: number;
  /** Whether to show first/last page buttons */
  showFirstButton?: boolean;
  /** Whether to show previous/next buttons */
  showPreviousNext?: boolean;
  /** Whether to show first/last page buttons */
  showLastButton?: boolean;
  /** Current page size */
  pageSize?: number;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Optional label for page size selector */
  pageSizeLabel?: string;
  /** Whether to center the pagination (default: false, aligns right) */
  centered?: boolean;
}

/**
 * Pagination component similar to MUI's Pagination
 * Shows page numbers with navigation controls
 */
export const Pagination = ({
  page,
  count,
  onChange,
  siblingCount = 1,
  showPreviousNext = true,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  pageSizeLabel,
  centered = false,
}: PaginationProps): React.ReactNode => {
  const handlePrevious = (): void => {
    if (page > 1) {
      onChange(page - 1);
    }
  };

  const handleNext = (): void => {
    if (page < count) {
      onChange(page + 1);
    }
  };

  const handlePageClick = (pageNumber: number): void => {
    onChange(pageNumber);
  };

  // Generate page numbers to show
  const getPageNumbers = (): string[] => {
    const pages: string[] = [];

    if (count <= 7) {
      // Show all pages if total is small
      for (let i = 1; i <= count; i++) {
        pages.push(String(i));
      }
    } else {
      // Show first page
      pages.push('1');

      // Show ellipsis if needed
      if (page - siblingCount > 2) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, page - siblingCount);
      const end = Math.min(count - 1, page + siblingCount);

      for (let i = start; i <= end; i++) {
        pages.push(String(i));
      }

      // Show ellipsis if needed
      if (page + siblingCount < count - 1) {
        pages.push('...');
      }

      // Show last page
      if (count > 1) {
        pages.push(String(count));
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const showPageSizeControls =
    pageSize !== undefined &&
    !!pageSizeOptions?.length &&
    typeof onPageSizeChange === 'function';

  const handlePageSizeChange = (value: string): void => {
    const nextSize = Number(value);
    if (!Number.isNaN(nextSize)) {
      onPageSizeChange?.(nextSize);
    }
  };

  const pageSizeSelectOptions = pageSizeOptions?.map((size) => ({
    label: String(size),
    value: String(size),
  }));

  const pageNumber = (pageNum: string, index: number): React.ReactNode => {
    switch (pageNum) {
      case '...':
        return (
          <SizableText
            key={`ellipsis-${index}`}
            fontSize="$2"
            color="$color"
            paddingHorizontal="$2"
          >
            ...
          </SizableText>
        );
      case `${page}`:
        return (
          <CurrentPageContainer>
            <SizableText color="$tertiary" fontSize="$2">
              {page}
            </SizableText>
          </CurrentPageContainer>
        );
      default: {
        const pageNumNumber = +pageNum;
        if (!pageNumNumber) return '';

        return (
          <PageButton
            key={pageNum}
            onPress={() => handlePageClick(pageNumNumber)}
          >
            {pageNum}
          </PageButton>
        );
      }
    }
  };

  const getJustifyContent = (): 'center' | 'right' | 'space-between' => {
    if (showPageSizeControls) return 'space-between';
    if (centered) return 'center';
    return 'right';
  };

  return (
    <XStack
      alignItems="center"
      justifyContent={getJustifyContent()}
      gap="$2"
      backgroundColor="transparent"
      width="100%"
      paddingVertical="$2"
    >
      {showPageSizeControls && (
        <XStack gap="$2" alignItems="center">
          {pageSizeLabel ? (
            <SizableText color="$secondary75" fontSize="$2">
              {pageSizeLabel}
            </SizableText>
          ) : null}
          <XStack>
            <Select
              options={pageSizeSelectOptions ?? []}
              value={String(pageSize)}
              onValueChange={handlePageSizeChange}
              placeholder={String(pageSize)}
              triggerSize="$1"
            />
          </XStack>
        </XStack>
      )}

      <XStack alignItems="center" gap="$2">
        {/* Previous button */}
        {showPreviousNext && (
          <Button
            size="$1"
            onPress={handlePrevious}
            disabled={page === 1}
            icon={<Ionicons name="chevron-back" size={20} color="inherit" />}
          />
        )}

        {/* Page numbers */}
        {pageNumbers.map((pageNum, index) => (
          <View key={`page-${index}`}>{pageNumber(pageNum, index)}</View>
        ))}

        {/* Next button */}
        {showPreviousNext && (
          <Button
            size="$1"
            onPress={handleNext}
            disabled={page === count}
            icon={<Ionicons name="chevron-forward" size={20} color="inherit" />}
          />
        )}
      </XStack>
    </XStack>
  );
};
